'use client';

import { FC } from 'react';
import cx from 'classnames';
import {
  MergedPriceAndAvailabilityResponse,
  ProductDealerPriceAvailability,
  ReplacementParts
} from 'services/dealerPriceAvailability/types';
import { Conditional } from '@cat-ecom/pcc-components';
import { checkIsRiyal } from 'app/[lang]/[esite]/utils/paymentUtils';
import { isEmpty } from '@cat-ecom/common/utils/commonUtils';
import DiscountPrice from './discount-price';
import { FormattedPriceWithCurrency } from './formatted-price-with-currency/formatted-price-with-currency';
import { RiyalPriceFontSize } from '@cat-ecom/pcc-components/src/constants/commonConstants';

interface PriceProps {
  currency: string;
  price?:
    | MergedPriceAndAvailabilityResponse
    | ProductDealerPriceAvailability
    | ReplacementParts;
  showDiscountSection?: boolean;
  isCoreDepositMobileDrawer?: boolean;
  isMobileView?: boolean;
  isRTL?: boolean;
  currencyLdFeatureFlag?: boolean;
  isLeftAlignPriceTag?: boolean;
  amount?: string;
  customStyles?: {
    container?: string;
    price?: string;
    currency?: string;
  };
  customTestIdPrefix?: string;
  sizeforRiyalFormattedPrice?: RiyalPriceFontSize;
}

const Price: FC<PriceProps> = ({
  price,
  currency = '',
  showDiscountSection = true,
  isCoreDepositMobileDrawer = false,
  isMobileView = false,
  isRTL = false,
  currencyLdFeatureFlag = false,
  isLeftAlignPriceTag = false,
  amount = '',
  customStyles = {},
  customTestIdPrefix = '',
  sizeforRiyalFormattedPrice = RiyalPriceFontSize.SM
}) => {
  const saudiCurrencySymbol = currencyLdFeatureFlag && checkIsRiyal(currency);
  const showPrice = !isEmpty(price?.totalPrice);
  const isNotUnitPriceAndNotSingleItem =
    price?.unitPrice !== price?.totalPrice && price?.quantity > 1;
  const customStylesWithEaLabel = {
    container: '',
    price: '',
    currency: cx('px-1', {
      'cat-u-theme-typography-label-lg': !isMobileView,
      'fw-normal': isMobileView
    })
  };
  const getPriceForDiscountedOrUnitPriceWithEaLabel = (priceAmount) => {
    const containerStyles = saudiCurrencySymbol
      ? ''
      : cx(
          { color: 'var(--cat-color-neutral-60)' },
          {
            'cat-u-theme-typography-body-sm': !isMobileView,
            'mb-0 cat-u-theme-typography-footnote': isMobileView
          }
        );
    const formattedRiyalPriceStyle = saudiCurrencySymbol
      ? cx('px-1', {
          'cat-u-theme-typography-body-sm': !isMobileView,
          'mb-0 cat-u-theme-typography-footnote': isMobileView
        })
      : '';
    const sizeforCurrencyText = saudiCurrencySymbol
      ? RiyalPriceFontSize.XXS
      : RiyalPriceFontSize.SM;
    return (
      <Conditional test={isNotUnitPriceAndNotSingleItem}>
        <div
          data-testid="discounted-or-unit-price-with-ea-label"
          className={containerStyles}
        >
          <FormattedPriceWithCurrency
            amount={priceAmount}
            currency={currency}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
            customStyles={{
              container: '',
              price: saudiCurrencySymbol
                ? cx(formattedRiyalPriceStyle, 'cat-u-theme-typography-sm px-1')
                : 'cat-u-theme-typography-sm px-1 ',
              currency: ''
            }}
            isRTL={saudiCurrencySymbol ? isRTL : false}
            replaceCurrencyWithEaLabel={true}
            sizeforRiyalFormattedPrice={
              sizeforRiyalFormattedPrice ?? sizeforCurrencyText
            }
            customTestIdPrefix={customTestIdPrefix || 'price-with-ea-label'}
          />
        </div>
      </Conditional>
    );
  };
  const getPriceSectionWhenAddtoCardEnabled = (amount) => {
    return (
      price?.enableAddToCart && (
        <div
          data-testid="formatted-price-for-enabled-add-to-cart"
          className={cx('m-0', {
            'pt-1': isMobileView && !isLeftAlignPriceTag
          })}
        >
          <FormattedPriceWithCurrency
            amount={amount}
            currency={currency}
            currencyLdFeatureFlag={currencyLdFeatureFlag}
            customStyles={customStylesWithEaLabel}
            isRTL={saudiCurrencySymbol ? isRTL : false}
            customTestIdPrefix={customTestIdPrefix || 'cart-enabled'}
            sizeforRiyalFormattedPrice={sizeforRiyalFormattedPrice}
          />
        </div>
      )
    );
  };
  const getDiscountSection = (priceObject) => {
    return (
      <Conditional test={showDiscountSection}>
        <DiscountPrice
          price={priceObject}
          isCoreDepositMobileDrawer={isCoreDepositMobileDrawer}
          isMobile
          currency={currency}
          isRTL={isRTL}
          currencyLdFeatureFlag={currencyLdFeatureFlag}
        />
      </Conditional>
    );
  };
  const getEnabledAddToCartDiscountAndPrice = () => {
    const priceAmount = price?.discountedPrice
      ? price?.discountedPrice
      : (price?.unitPrice ?? '');
    return (
      showPrice && (
        <>
          {getPriceSectionWhenAddtoCardEnabled(price?.totalPrice)}
          {getPriceForDiscountedOrUnitPriceWithEaLabel(priceAmount)}
          {getDiscountSection(price)}
        </>
      )
    );
  };
  const getPrice = () => {
    return !isEmpty(price) ? (
      getEnabledAddToCartDiscountAndPrice()
    ) : (
      <FormattedPriceWithCurrency
        amount={amount}
        currency={currency}
        currencyLdFeatureFlag={currencyLdFeatureFlag}
        customStyles={customStyles}
        isRTL={saudiCurrencySymbol ? isRTL : false}
        customTestIdPrefix={customTestIdPrefix}
        sizeforRiyalFormattedPrice={sizeforRiyalFormattedPrice}
      />
    );
  };
  return getPrice();
};

export default Price;
